import { useCallback, useMemo } from "react";
import { useAppDispatch, useAppSelector } from "@/services/store";
import {
  useCreateNoteMutation,
  useDeleteFileByIdMutation,
  useGetAllPersonsQuery,
  useLazyGetNoteByIdQuery,
  useUpdateNoteMutation,
} from "@/services/api";
import { NoteEditor } from "@/features/NoteEditor";
import { isNotVoid } from "@/shared/utils";
import { useAlert } from "@/shared/hooks";
import { getErrorMessage } from "@/services/errors";
import {
  prepareCreateNotePayload,
  prepareUpdateNotePayload,
  type TabsManager,
} from "@/features/notes-manager";
import type { NoteEditorFormValues } from "@/features/NoteEditor/model";
import type { Person } from "@/services/api";
import type {
  CreateNoteRequest,
  Note,
  UpdateNoteRequest,
} from "@/services/api/entities/note/types";
import type { TabImproved } from "@/shared/Components";
import { notesContentActions, notesContentSelectors } from "../slice";

export const useTabsManager = (): TabsManager => {
  const dispatch = useAppDispatch();

  const activeNote = useAppSelector(notesContentSelectors.selectActiveNote);
  const selectedNotes = useAppSelector(notesContentSelectors.selectSelectedNotes);

  const { data: personsResponse } = useGetAllPersonsQuery();

  const [updateNote] = useUpdateNoteMutation();
  const [getNoteById] = useLazyGetNoteByIdQuery();
  const [createNote] = useCreateNoteMutation();
  const [deleteNoteFile] = useDeleteFileByIdMutation();

  const { callError } = useAlert();

  const upsertNote = useCallback((data: NoteEditorFormValues["currentNote"]) => {
    const isTempNote = data.id?.includes("new-");
    const payload = isTempNote ? prepareCreateNotePayload(data) : prepareUpdateNotePayload(data);
    const promise = isTempNote
      ? createNote(payload as CreateNoteRequest)
      : updateNote(payload as UpdateNoteRequest);

    promise
      .unwrap()
      .then((res) => {
        const person = personsResponse?.persons.find((pers) => pers.id === res.personId);

        if (isTempNote) {
          dispatch(notesContentActions.updateDraftNoteId({ tempId: data.id!, newId: res.id }));
        }

        dispatch(notesContentActions.updateActiveNote({ ...res, person: person ?? null }));
        dispatch(notesContentActions.setSelectedIds([res.id]));
      })
      .catch((e) => {
        callError({ text: getErrorMessage(e.code) });
      });
  }, []);

  const handleTabChange = useCallback(
    (noteId: string) => {
      dispatch(notesContentActions.setActiveNote({ noteId }));
    },
    [dispatch],
  );

  const handleCloseTab = useCallback(
    (noteId: string) => {
      dispatch(notesContentActions.removeSelectedNote({ noteId }));
    },
    [dispatch],
  );

  const deleteNoteFileById = ({
    file,
    noteId,
  }: {
    file: Note["attachments"][number];
    noteId: string;
  }) => {
    deleteNoteFile({ fileId: file.fileDto.fileId })
      .unwrap()
      .catch((e) => {
        callError({ text: getErrorMessage(e.code) });
      })
      .then(() => {
        getNoteById({ noteId })
          .unwrap()
          .then((note) => {
            dispatch(
              notesContentActions.updateActiveNote({
                ...note,
                person: personsResponse?.persons.find(
                  (person) => note.personId === person.id,
                ) as Person,
              }),
            );
          });
      });
  };

  const tabs: TabImproved[] = useMemo(() => {
    return selectedNotes.map((note) => ({
      key: note.id,
      label: note.title ?? "",
      children: (
        <NoteEditor
          onFileDelete={(file) => deleteNoteFileById({ file: file, noteId: note.id })}
          currentNote={note}
          onSubmit={upsertNote}
        />
      ),
    }));
  }, [selectedNotes, upsertNote]);

  return {
    state: {
      editorOpen: isNotVoid(selectedNotes),
      tabs,
      activeTab: activeNote?.id ?? "",
    },
    methods: {
      handleTabChange,
      handleCloseTab,
      deleteNoteFileById,
      upsertNote,
    },
  };
};
